import { Database, MapPin, Star } from "lucide-react";

import type { Operation } from "@/components/Operations";

const BARS = [38, 62, 46, 80, 58, 92];

const CODE_LINES = [
  [{ w: "w-6", c: "bg-brand/60" }, { w: "w-10", c: "bg-ink-subtle/30" }],
  [{ w: "w-3", c: "bg-transparent" }, { w: "w-8", c: "bg-brand-soft/70" }, { w: "w-12", c: "bg-ink-subtle/25" }],
  [{ w: "w-3", c: "bg-transparent" }, { w: "w-14", c: "bg-ink-subtle/25" }],
  [{ w: "w-6", c: "bg-transparent" }, { w: "w-5", c: "bg-brand/60" }, { w: "w-9", c: "bg-brand-soft/70" }],
  [{ w: "w-3", c: "bg-transparent" }, { w: "w-10", c: "bg-ink-subtle/25" }],
];

function WindowBar({ url }: { url?: boolean }) {
  return (
    <div className="flex items-center gap-1 px-2 h-4 border-b border-line">
      <span className="h-1 w-1 rounded-full bg-ink-subtle/40" />
      <span className="h-1 w-1 rounded-full bg-ink-subtle/40" />
      <span className="h-1 w-1 rounded-full bg-ink-subtle/40" />
      {url && <span className="ml-1.5 h-1.5 flex-1 rounded-full bg-surface-sunken" />}
    </div>
  );
}

function ProductPreview() {
  return (
    <div className="absolute inset-x-5 top-4 bottom-0 flex flex-col rounded-t-md border border-b-0 border-line bg-surface-raised">
      <WindowBar />

      <div className="flex flex-1 min-h-0">
        <div className="flex w-9 flex-col gap-1.5 border-r border-line p-1.5">
          <span className="h-1 rounded-full bg-brand/40" />
          <span className="h-1 rounded-full bg-ink-subtle/20" />
          <span className="h-1 rounded-full bg-ink-subtle/20" />
          <span className="h-1 rounded-full bg-ink-subtle/20" />
        </div>

        <div className="flex flex-1 flex-col gap-1.5 p-2">
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1/3 rounded-full bg-ink-subtle/30" />
            <span className="ml-auto h-2.5 w-6 rounded-sm bg-brand/80" />
          </div>

          <div className="flex flex-1 items-end gap-1">
            {BARS.map((height, index) => (
              <span
                key={index}
                className="op-bar flex-1 rounded-t-[2px] bg-brand/25 last:bg-brand/70"
                style={{ height: `${height}%`, animationDelay: `${index * -0.35}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CodePreview() {
  return (
    <>
      <div className="absolute left-5 right-10 top-4 bottom-0 flex flex-col rounded-t-md border border-b-0 border-line bg-surface-raised">
        <WindowBar />

        <div className="flex flex-col gap-1.5 p-2">
          {CODE_LINES.map((tokens, line) => (
            <div key={line} className="flex items-center gap-1">
              <span className="w-2 text-right font-mono text-[6px] leading-none text-ink-subtle/60 tabular-nums">
                {line + 1}
              </span>
              {tokens.map((token, index) => (
                <span key={index} className={`h-1 rounded-full ${token.w} ${token.c}`} />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div
        className="absolute right-3 bottom-3 flex items-center gap-1.5 rounded-md border border-line bg-surface-raised px-1.5 py-1 shadow-[0_6px_16px_rgba(11,27,58,0.08)]"
        style={{ animation: "service-drift 5.5s ease-in-out infinite" }}
      >
        <Database className="h-3 w-3 text-brand" strokeWidth={2.25} />
        <span className="op-pulse h-1.5 w-1.5 rounded-full bg-brand-soft" />
      </div>
    </>
  );
}

function PresencePreview() {
  return (
    <>
      <div className="absolute left-4 top-4 bottom-0 w-[62%] flex flex-col rounded-t-md border border-b-0 border-line bg-surface-raised">
        <WindowBar url />

        <div className="flex flex-col gap-1.5 p-2">
          <span className="h-7 rounded-sm bg-brand/15" />
          <span className="h-1 w-3/4 rounded-full bg-ink-subtle/30" />
          <span className="h-1 w-1/2 rounded-full bg-ink-subtle/20" />
        </div>
      </div>

      <div
        className="absolute right-4 top-3 flex"
        style={{ animation: "service-drift 5.5s ease-in-out infinite" }}
      >
        <span className="h-4 w-4 rounded-full border-2 border-surface-raised bg-brand" />
        <span className="-ml-1.5 h-4 w-4 rounded-full border-2 border-surface-raised bg-brand-soft" />
        <span className="-ml-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-line bg-surface-raised font-sans text-[7px] text-ink">
          Aa
        </span>
      </div>

      <div
        className="absolute right-3 bottom-3 w-[44%] rounded-md border border-line bg-surface-raised p-1.5 shadow-[0_6px_16px_rgba(11,27,58,0.08)]"
        style={{ animation: "service-drift 5.5s ease-in-out infinite", animationDelay: "-2.2s" }}
      >
        <div className="flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0 text-brand" strokeWidth={2.25} />
          <span className="h-1 flex-1 rounded-full bg-ink-subtle/30" />
        </div>
        <div className="mt-1 flex gap-px text-brand-soft">
          {Array.from({ length: 5 }, (_, index) => (
            <Star key={index} className="h-2 w-2 fill-current" strokeWidth={0} />
          ))}
        </div>
      </div>
    </>
  );
}

export default function OperationVisual({
  kind,
  services,
}: Pick<Operation, "kind" | "services">) {
  const preview =
    kind === "presence" ? (
      <PresencePreview />
    ) : services.includes("fullstack") ? (
      <CodePreview />
    ) : (
      <ProductPreview />
    );

  return (
    <div
      className="relative h-24 overflow-hidden border-b border-line bg-surface-sunken/60"
      aria-hidden="true"
    >
      {preview}
    </div>
  );
}
